import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import ResultsSummaryCard from './ResultsSummaryCard';
import DownloadButtons from './DownloadButtons';

/**
 * ResultsStep displays the final import results including:
 * - Status message based on job outcome
 * - Summary card with row counts
 * - Download buttons for available reports
 * - "Import Another File" button to reset the wizard
 */
const ResultsStep: React.FC = () => {
  const jobStatus = useImportWizardStore((s) => s.jobStatus);
  const resetWizard = useImportWizardStore((s) => s.resetWizard);

  const getStatusMessage = () => {
    if (!jobStatus) return null;

    switch (jobStatus.status) {
      case 'completed':
        return (
          <Alert severity="success" sx={{ mb: 2 }}>
            Import completed successfully! All rows were processed.
          </Alert>
        );
      case 'completed_with_errors':
        return (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Import completed with some errors. Review the failed rows below.
          </Alert>
        );
      case 'failed':
        return (
          <Alert severity="error" sx={{ mb: 2 }}>
            Import failed. {jobStatus.error || 'An unexpected error occurred.'}
          </Alert>
        );
      default:
        return null;
    }
  };

  return (
    <Box data-testid="results-step">
      <Typography variant="h6" gutterBottom>
        Import Results
      </Typography>

      {getStatusMessage()}

      {jobStatus && (
        <>
          <ResultsSummaryCard
            totalRows={jobStatus.totalRows}
            successfulRows={jobStatus.successfulRows}
            failedRows={jobStatus.failedRows}
            failedRowRanges={jobStatus.failedRowRanges}
          />

          <DownloadButtons downloads={jobStatus.downloads} />
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={resetWizard}
          data-testid="import-another-file-btn"
        >
          Import Another File
        </Button>
      </Box>
    </Box>
  );
};

export default ResultsStep;
